import {
  type AssignmentCopyRequest,
  assignmentFolderId,
  assignmentFolders,
  type Classroom,
  type HomeworkAssignment,
} from "@classroom/domain";
import { useState } from "react";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { useSaveBeforeUnload } from "./lib/useSaveBeforeUnload";
import "./teacher-workflows.css";

export default function AssignmentCopyDialog({
  source,
  classroom,
  classes,
  dateRange,
  readOnly,
  onSave,
  onClose,
}: {
  source: HomeworkAssignment;
  classroom: Classroom;
  classes: Classroom[];
  dateRange?: { startDate?: string; endDate?: string };
  readOnly?: boolean;
  onSave: (request: AssignmentCopyRequest) => Promise<unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation("assignmentRecords");
  const [destinations] = useState(() =>
    classes.filter(
      (item) =>
        item.active &&
        item.classRole === "teaching" &&
        item.id !== classroom.id &&
        item.workspaceId === classroom.workspaceId &&
        item.schoolYearId === classroom.schoolYearId,
    ),
  );
  const [createdAt] = useState(() => new Date().toISOString());
  const [choices, setChoices] = useState(() => {
    const sourceFolders = assignmentFolders(classroom);
    const sourceFolder = sourceFolders.find((item) => item.id === assignmentFolderId(source, sourceFolders));
    return destinations.map((item) => ({
      classId: item.id,
      id: `assignment_copy_${crypto.randomUUID()}`,
      dueDate: source.dueDate,
      selected: false,
      folderId: assignmentFolders(item).find((folder) => folder.name === sourceFolder?.name)?.id || "uncategorized",
    }));
  });
  const [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const selected = choices.filter((item) => item.selected);
  const close = () => {
    if (!saving && (!selected.length || window.confirm(t("copy.discard")))) onClose();
  };
  const ref = useDialogFocus(true, close);
  useSaveBeforeUnload(saving || selected.length > 0);
  const update = (id: string, patch: Partial<(typeof choices)[number]>) =>
    setChoices((current) => current.map((item) => (item.classId === id ? { ...item, ...patch } : item)));
  async function save() {
    if (readOnly || saving || !selected.length) return;
    setSaving(true);
    setError("");
    try {
      await onSave({ source, targets: selected.map(({ selected: _selected, ...target }) => target), createdAt });
      onClose();
    } catch (caught) {
      setError(t(caught instanceof Error && caught.message === "stale-copy" ? "copy.stale" : "copy.failed"));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="assignment-folder-overlay">
      <div
        className="paper-card workflow-dialog workflow-dialog-wide"
        role="dialog"
        aria-modal="true"
        aria-label={t("copy.title")}
        tabIndex={-1}
        ref={ref}
        data-autosave
        data-save-state={saving ? "saving" : selected.length ? "dirty" : "saved"}
      >
        <h3>{t("copy.title")}</h3>
        <div className="workflow-copy-source">
          <b>{source.title}</b>
          <span>
            {classroom.name} · {source.dueDate}
          </span>
        </div>
        <p className="workflow-context">{t("copy.description")}</p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="workflow-copy-list">
            {destinations.map((item) => {
              const choice = choices.find((row) => row.classId === item.id)!;
              return (
                <div key={item.id} className={`workflow-copy-row${choice.selected ? " selected" : ""}`}>
                  <label className="workflow-class-choice">
                    <input
                      type="checkbox"
                      checked={choice.selected}
                      aria-label={t("copy.select", { name: item.name })}
                      disabled={readOnly || saving}
                      onChange={(event) => update(item.id, { selected: event.target.checked })}
                    />
                    <b>{item.name}</b>
                  </label>
                  {choice.selected && (
                    <div className="workflow-copy-settings">
                      <label>
                        {t("copy.date")}
                        <input
                          type="date"
                          value={choice.dueDate}
                          required
                          min={dateRange?.startDate}
                          max={dateRange?.endDate}
                          aria-label={t("copy.dateLabel", { name: item.name })}
                          disabled={readOnly || saving}
                          onChange={(event) => update(item.id, { dueDate: event.target.value })}
                        />
                      </label>
                      <label>
                        {t("copy.folder")}
                        <select
                          value={choice.folderId}
                          aria-label={t("copy.folderLabel", { name: item.name })}
                          disabled={readOnly || saving}
                          onChange={(event) => update(item.id, { folderId: event.target.value })}
                        >
                          {assignmentFolders(item).map((folder) => (
                            <option value={folder.id} key={folder.id}>
                              {folder.name}
                            </option>
                          ))}
                          <option value="uncategorized">{t("folders.uncategorized")}</option>
                        </select>
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
            {!destinations.length && <p className="empty-copy">{t("copy.empty")}</p>}
          </div>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="assignment-folder-dialog-actions">
            <button type="button" disabled={saving} onClick={close}>
              {t("copy.cancel")}
            </button>
            <button type="submit" className="primary-action" disabled={readOnly || saving || !selected.length}>
              {saving ? t("copy.saving") : t("copy.apply", { count: selected.length })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
